import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import BaziChart from "../../../custom/bazi-chart/bazi-chart";
import FuyaoziHexagram from "../hexagram/fuyaozi-hexagram";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../../../dialog/confirm/confirm-dialog";
import useAuth from "../../../../hooks/useAuth";
import { useLoginGate } from "../../../../context/login-gate";
import { needsGender } from "../../../../utils/birth-input";
import "./fuyaozi-chat-style.scss";

//伏爻子的頭像。放在 public/ 而不是 import 進來：
//  角色圖是內容而不是程式資產，換圖時直接覆蓋檔案就好，不用重新編譯。
const MASTER_AVATAR = {
  backgroundImage: `url(${process.env.PUBLIC_URL}/images/characters/fuyaozi.jpg)`,
};

const MASTER = "伏爻子";

//輸入框長到這個高度就不再長，改成自己捲（約五行）。
//  不設上限的話，貼一大段進來會把整個聊天室頂掉。
const MAX_INPUT_HEIGHT = 120;

//問卦 — 聊天（框架）
//
//  接待的是伏爻子（文王聖卦）。他的設定是「只答你問的那一件事」，
//  所以空狀態與輸入提示都在引導使用者把問題問窄 ——
//  卦本來就是一卦一事，問得越散，答得越不準。
//
//  訊息的形狀（接後端時照這個給）：
//    { id, role: "user" | "master", name?, text, blocks? }
//  name 沒給就用 MASTER —— 單則訊息可以自己指定發話者
//  blocks 是伏爻子的訊息可夾帶的結構化內容，跟著訊息一起下來而不是另外查表：
//    { type: "hexagram", data: { gua } }              卦象圖（卦名 + 六爻）
//    { type: "section",  data: { title, text, collapsed? } }  一段有小標的文字
//        collapsed: true 代表這一段可以折、而且預設折起來
//    { type: "chart",    data: { title, columns[] } } 命盤表
//  這樣一則訊息就是完整的一份資料，不用在前端拼湊。
//
//  斷語就是這樣組起來的：卦象圖 → 專業剖析 → 白話解析，整包在同一個對話框裡。
//  訊息由頁面那一層持有（見 fuyaozi-ask-page 的說明）——
//  覆述動畫演完要把問題貼進來，之後斷語也要從後端進同一條列表，
//  兩件事都發生在這支元件之外。
//  伏爻子回覆裡「有小標的一段」。
//
//  抽成獨立元件只有一個理由：它要自己記得開合，而它是在 messages.map 裡面
//  畫出來的 —— hook 不能寫在 map 裡。
//
//  collapsed 為 true 代表「可以折，而且預設是折起來的」。沒給就是不能折、
//  一直攤開（白話解析就是這一種）。
const ReplySection = ({ title, text, collapsed }) => {
  const collapsible = collapsed !== undefined;
  const [open, setOpen] = useState(!collapsed);

  //模型用空行分段。split 之後逐段畫，
  //  直接塞一整串的話換行會被 HTML 吃掉。
  const paragraphs = String(text)
    .split(/\n\s*\n/)
    .filter(Boolean);

  const heading = collapsible ? (
    //  button 包在 h4 裡面而不是反過來 —— button 的內容只能是 phrasing content，
    //  把 h4 塞進去是不合法的，而標題的語意要留著。
    //  整條標題列都可以按，不是只有那個箭頭：手機上 12px 的箭頭太難點。
    <h4 className="fuyaozi-chat__section-title">
      <button
        type="button"
        className="fuyaozi-chat__section-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span>{title}</span>
        <svg
          className={`fuyaozi-chat__chevron${open ? " is-open" : ""}`}
          viewBox="0 0 24 24"
          width="14"
          height="14"
          aria-hidden="true"
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2.2"
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </h4>
  ) : (
    <h4 className="fuyaozi-chat__section-title">{title}</h4>
  );

  return (
    <section className="fuyaozi-chat__section">
      {heading}
      {/* 折起來的時候整段不掛在 DOM 上。用 display:none 藏也可以，
          但斷語一段有三四百字，留著等於讓搜尋與朗讀都讀得到看不見的內容。 */}
      {open &&
        paragraphs.map((para, k) => (
          <p className="fuyaozi-chat__text" key={k}>
            {para.trim()}
          </p>
        ))}
    </section>
  );
};

const FuyaoziChat = ({
  messages = [],
  onSend,
  error = "",
  ready = true,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}) => {
  const { user, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  //  性別沒填時跳的那一張。true = 顯示
  const [needGender, setNeedGender] = useState(false);
  const { requireLogin } = useLoginGate();
  const [draft, setDraft] = useState("");
  const inputRef = useRef(null);

  //—— 輸入框跟著內容長高 ——
  //
  //  Enter 是斷行不是送出（Kevin 2026-10-08），所以這裡是 textarea 而不是
  //  input。textarea 的高度不會自己跟著內容走，要自己量：
  //  先把高度歸零才量得到真正的 scrollHeight（不歸零的話它只會變高不會變矮，
  //  刪字之後框會一直卡在最高的那個高度）。
  //
  //  寫在 effect 裡而不是 onChange：送出之後是用 setDraft("") 清掉的，
  //  那一條路徑不經過 onChange，掛在那裡會讓框停在三行高。
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return undefined;

    const fit = () => {
      el.style.height = "auto";
      const want = el.scrollHeight;
      el.style.height = `${Math.min(want, MAX_INPUT_HEIGHT)}px`;
      //  —— 只有真的裝不下才開捲軸 ——
      //
      //  一直掛著 overflow-y: auto 的話，內容只要比框高一兩個 px
      //  就會冒出一條捲軸（macOS 是一截白色的拇指壓在框的右緣，
      //  Kevin 2026-10-12 回報的「不明的白色標記」就是它）。
      //  高度本來就是照內容算的，沒到上限時根本不該能捲。
      el.style.overflowY = want > MAX_INPUT_HEIGHT ? "auto" : "hidden";
    };

    fit();

    //  —— 字型換好之後要再量一次 ——
    //
    //  正文是 Google Fonts 的 Noto Serif TC，載完才會換上去。
    //  換字型會讓同一行變高一兩個 px，而這支 effect 的依賴只有 draft ——
    //  不補量的話那一兩個 px 就一直溢出，捲軸會一直掛在那裡。
    let alive = true;
    document.fonts?.ready?.then(() => {
      if (alive) fit();
    });
    return () => {
      alive = false;
    };
  }, [draft]);

  const topRef = useRef(null);
  //  往回載入之前的頁面總高度。有值代表「這一次 messages 變動是往前接的」。
  const anchorRef = useRef(null);
  //  上一次看到的第一則與最後一則是誰。用來分辨「往回接了一段」與「有新訊息」。
  const firstIdRef = useRef(null);
  const lastIdRef = useRef(null);
  //  還沒畫過任何訊息。第一次灌進來（載歷史）要捲到底，之後才是停在開頭。
  const wasEmptyRef = useRef(true);
  //  訊息列表的容器，用來找「要捲到哪一則」
  const listRef = useRef(null);

  //—— 捲動：捲到某一則的開頭，或捲到最底 ——
  //
  //  斷語落地時要停在**那一則的開頭**（Kevin 2026-10-08 決定）——
  //  一則斷語有卦象圖加三段，置底的話一打開就是「給你的建議」的結尾，
  //  等於從最後一句開始讀。對齊開頭才讀得下去。
  //  避開頂部列是用 CSS 的 scroll-margin-top 做的（見樣式檔），
  //  那個高度是版面的事，不該讓這支元件知道。
  //
  //  進頁面載入歷史時則是捲到最底 —— 那是聊天室的常態，要看最近的一卦。
  //
  //  —— 為什麼要捲三次 ——
  //    立刻      畫面先到位，不要閃
  //    下一幀    斷語把「容我細看片刻」換成一大塊內容時，瀏覽器的**捲動錨定**
  //              會把那一塊維持在原來的位置，等於把我們的捲動撤銷。
  //              錨定是在同一幀內做的，下一幀再捲一次才蓋得過去。
  //    250ms 後  字型換好、頭像載完之後高度還會再長一點。
  const scrollTo = useCallback((msgId) => {
    const go = () => {
      const list = listRef.current;
      if (!list) return;
      if (!msgId) {
        window.scrollTo(0, document.documentElement.scrollHeight);
        return;
      }
      //  用屬性選擇器而不是存一堆 ref —— 訊息會整個換掉（pending-xxx → xxx），
      //  ref 要跟著搬，而這裡只需要「現在畫面上那一則」。
      //  CSS.escape：卦號是我們自己產的，但別讓選擇器因為奇怪字元就炸掉。
      const el = list.querySelector(`[data-msg="${CSS.escape(msgId)}"]`);
      if (el) el.scrollIntoView({ block: "start" });
      else window.scrollTo(0, document.documentElement.scrollHeight);
    };
    go();
    const raf = requestAnimationFrame(go);
    const timer = setTimeout(go, 250);
    //  訊息又變了就把還沒跑的那兩次收掉，不然會跟新的那一輪打架
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, []);

  //—— 捲動：新訊息捲到底，往回載入則留在原地 ——
  //
  //  這兩件事一定要分開。一律捲到底的話，往回翻一頁就會被彈回最新那一卦，
  //  等於翻不動；一律不捲的話，斷語生成完會掉在畫面外。
  //
  //  用 useLayoutEffect 而不是 useEffect：要在瀏覽器畫出來之前就把捲軸補回去，
  //  不然會先看到畫面往上跳一大段再被拉回來。
  useLayoutEffect(() => {
    const firstId = messages[0]?.id ?? null;
    const lastId = messages[messages.length - 1]?.id ?? null;

    //  —— 怎麼分辨這次變動是哪一種 ——
    //
    //  往回接了一段 → **第一則**換人了
    //  有新訊息     → **最後一則**換人了（斷語回來時 id 會從 pending-xxx
    //                 變成 xxx，那也算 —— 那一刻正好是斷語落地）
    //
    //  ⚠️ 不要只看 anchorRef 有沒有值就判定是往回接。踩過：
    //  哨兵一進畫面就會把 anchorRef 設好，但 onLoadMore 可能直接早退
    //  （沒有游標、或正在載入中），那個值就留在那裡沒人清 ——
    //  等斷語回來時這支 effect 會走進「往回接」的分支做 scrollBy，
    //  畫面被釘在伏爻子訊息的開頭，不會置底（Kevin 在真機上發現）。
    const anchor = anchorRef.current;
    const prepended = anchor != null && firstId !== firstIdRef.current;
    const appended = Boolean(lastId) && lastId !== lastIdRef.current;

    //  不管這次是哪一種，anchor 都要清掉 —— 它只對緊接著的那一次有效
    anchorRef.current = null;
    firstIdRef.current = firstId;
    lastIdRef.current = lastId;

    //  往回接了一段：頁面變高多少，捲軸就往下補多少，視線停在原來那一則上
    if (prepended) {
      const delta = document.documentElement.scrollHeight - anchor;
      if (delta) window.scrollBy(0, delta);
      return undefined;
    }

    if (!appended) return undefined;

    //  第一次把訊息灌進來（進頁面載歷史）→ 捲到最底，看最近的一卦。
    //  之後每次最後一則換人 → 停在那一則的開頭。
    const first = wasEmptyRef.current;
    wasEmptyRef.current = false;
    return scrollTo(first ? null : lastId);
  }, [messages, scrollTo]);

  //—— 捲到最上面就往回翻一頁 ——
  //
  //  用 IntersectionObserver 盯一個哨兵元素，不自己聽 scroll 事件 ——
  //  scroll 在行動裝置上一秒會觸發幾十次，每次都要讀 scrollTop 造成重排。
  //
  //  rootMargin 給 240px：讓它在哨兵還沒真的進畫面時就先去要資料，
  //  等捲到頂那一段通常已經接上了。
  useEffect(() => {
    const el = topRef.current;
    if (!el || !hasMore || loadingMore) return;

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        //  在發請求的這一刻記下高度。從這裡到資料接上為止，
        //  畫面上唯一會變高的就是接進來的那一段 ——
        //  載入中的那一列是固定高度的（見 __more），不然這個差值就不準。
        anchorRef.current = document.documentElement.scrollHeight;
        onLoadMore?.();
      },
      { rootMargin: "240px 0px 0px 0px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  const handleSend = (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;

    //  再擋一次。focus 那一道是體驗（讓他在打字之前就知道要登入），
    //  這一道是保險 —— 外接鍵盤、自動填入、瀏覽器還原草稿都可能繞過 focus。
    if (!isLoggedIn) {
      requireLogin(null, "登入後才能保留你的問卦紀錄，日後隨時回來看。");
      return;
    }

    //  性別也再擋一次。真正的攔截點在 handleFocus（打字之前就問），
    //  這一道跟上面的登入一樣是**保險** —— 外接鍵盤、自動填入、
    //  瀏覽器還原草稿都可能繞過 focus。
    //
    //  為什麼非擋不可：六爻取用神分男女（男占婚姻取妻財、女占婚姻取官鬼），
    //  不知道誰在問，感情類的卦會取錯用神，而且斷語照樣通順，看不出來錯了。
    if (needsGender(user)) {
      setNeedGender(true);
      return;
    }

    //沒券的檢查在頁面那一層（它才知道餘額，而且要負責把人導去購券分頁）。
    //  那只是體驗 —— 真正的把關是後端扣券時的原子檢查，
    //  前端擋的一律繞得過。
    //
    //  刻意不在這裡擋字數（後端的上限是 500 字，只為了擋灌文章）——
    //  打到一半打不下去會讓人以為壞了。
    //
    //  也刻意不擋登入：問卦這一頁外面包著 RequireUserAuth（見 App.js），
    //  走到這裡的人一定已經登入了。
    onSend?.(text);
    setDraft("");
  };

  //—— 登入的攔截點就在這裡 ——
  //
  //  這一頁刻意不擋在門口（見 App.js 的路由說明）：先讓他進來看到角色、
  //  看到問卦長什麼樣，等他真的要打字才跳登入 —— 那時候他已經想問了，
  //  才有理由答應。
  //
  //  手機上 focus 會把鍵盤推上來，而登入彈窗是置中的 —— 不先收鍵盤的話
  //  彈窗會被蓋掉一半。所以 blur 再跳。
  const handleFocus = (e) => {
    //—— 第一道：登入 ——
    if (!isLoggedIn) {
      e.currentTarget.blur();
      //  不帶 to：登入完成後留在這一頁繼續打字，不要把他導去別的地方
      requireLogin(null, "登入後才能保留你的問卦紀錄，日後隨時回來看。");
      return;
    }

    //—— 第二道：性別填了沒 ——
    //
    //  順序不能顛倒：沒登入的話根本沒有帳號可以讀性別，
    //  先問性別會變成「你是男是女」跳完才叫他登入，登入完又問一次。
    //
    //  跟登入同一個時機攔 —— 擋在打字之前而不是送出之後。
    //  打完一整句才被退回去填性別，那段字還會不見（白夜那邊同理，
    //  見 baiye-chat 的「第二道：生辰齊了沒」）。
    //
    //  同樣要先 blur：手機上鍵盤已經推上來了，而 dialog 是置中的，
    //  不收鍵盤會被蓋掉一半。
    if (needsGender(user)) {
      e.currentTarget.blur();
      setNeedGender(true);
    }
  };

  return (
    <div className="fuyaozi-chat">
      {/* 歷史還沒拉回來之前兩邊都不畫。
          畫空狀態的話，有二十卦的人會先看到「說一件事」再被歷史蓋掉。 */}
      {!ready ? null : messages.length === 0 ? (
        <div className="fuyaozi-chat__empty">
          <span
            className="fuyaozi-chat__empty-avatar"
            style={MASTER_AVATAR}
            aria-hidden="true"
          />
          <h2 className="fuyaozi-chat__empty-title">說一件事</h2>
          <p className="fuyaozi-chat__empty-desc">
            一卦一事。問得越明確，卦象答得越準。
            <br />
            「該不該接這份工作」勝過「我的事業運如何」。
          </p>

        </div>
      ) : (
        <div className="fuyaozi-chat__list" ref={listRef}>
          {/* 往回翻的哨兵。高度固定 —— 它會在「載入中」與「空的」之間切換，
              高度一變就會把上面那個 scrollHeight 的差值算歪。 */}
          {hasMore && (
            <div className="fuyaozi-chat__more" ref={topRef}>
              {loadingMore ? "正在翻出更早的卦…" : ""}
            </div>
          )}

          {messages.map((msg) =>
            msg.role === "user" ? (
              <div
                className="fuyaozi-chat__row fuyaozi-chat__row--user"
                key={msg.id}
                data-msg={msg.id}
              >
                <p className="fuyaozi-chat__bubble">{msg.text}</p>
              </div>
            ) : (
              <div className="fuyaozi-chat__row" key={msg.id} data-msg={msg.id}>
                <div className="fuyaozi-chat__master">
                  <span
                    className="fuyaozi-chat__avatar"
                    style={MASTER_AVATAR}
                    aria-hidden="true"
                  />
                  <span className="fuyaozi-chat__name">
                    {msg.name || MASTER}
                  </span>
                </div>

                {/* 伏爻子的回覆整包在一個對話框裡（Kevin 2026-10-08 要求）。
                    原本是裸露的文字，理由是「內容很長，包成泡泡會很擠」——
                    但斷語現在是結構化的（卦象圖 + 兩段），有個框反而
                    看得出「這是一則完整的回覆」而不是一坨字。 */}
                <div className="fuyaozi-chat__reply">
                  {/* 純文字的訊息（例如暫代的「容我細看片刻」）。
                      有 blocks 的訊息不給 text，所以這裡要判斷。 */}
                  {msg.text && (
                    <p className="fuyaozi-chat__text">{msg.text}</p>
                  )}

                  {msg.blocks?.map((block, i) => {
                    if (block.type === "hexagram") {
                      return <FuyaoziHexagram key={i} gua={block.data.gua} />;
                    }
                    if (block.type === "section") {
                      return <ReplySection key={i} {...block.data} />;
                    }
                    if (block.type === "chart") {
                      return <BaziChart key={i} data={block.data} />;
                    }
                    return null;
                  })}
                </div>
              </div>
            ),
          )}
        </div>
      )}

      {/* 付款／建單的錯誤。擺在輸入列上方而不是用 alert ——
          使用者剛填完發票，要能一邊看著原因一邊改。 */}
      {error && <p className="fuyaozi-chat__error">{error}</p>}

      {/* 輸入列固定在底部 */}
      <form className="fuyaozi-chat__composer" onSubmit={handleSend}>
        {/* 顯示是「誰」在問：合盤等情境下可能替別人提問 */}
        <span className="fuyaozi-chat__who">{user?.userName || "我"}</span>

        {/* textarea 而不是 input：Enter 要能斷行。
            input 在 form 裡按 Enter 會直接送出，那不是他要的 ——
            問題常常要分兩三句講清楚。送出只走右邊那顆鈕。 */}
        <textarea
          ref={inputRef}
          className="fuyaozi-chat__input"
          rows={1}
          onFocus={handleFocus}
          placeholder="請輸入你想問的問題"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          aria-label="輸入問題"
        />

        <button
          type="submit"
          className="fuyaozi-chat__send"
          //沒打字就不能送：按了沒反應會讓人以為壞了
          disabled={!draft.trim()}
          aria-label="送出"
        >
          {/* 紙飛機。實心而不是描邊 —— 這顆鈕是金底深色字，
              2px 的線在 36px 的圓裡會顯得虛，實心才撐得住。 */}
          <svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
          </svg>
        </button>
      </form>

      {/* 性別沒填。跟白夜那一張同一套做法：純告知 + 去填，
          不給「下次再說」的實質出口 —— 留在這一頁也問不了。
          點遮罩或取消就是留在原地。

          理由講真話：這是伏爻子會說的那種話，而且使用者有權知道
          我們為什麼要問。只寫「請補齊資料」會像在收集個資。

          確認後導去個人資料頁；那一頁存完會自己 navigate(-1) 回到這裡
          （見 personal-editor-page），所以不用把回程寫在這裡。 */}
      <ConfirmDialog
        open={needGender}
        title="先報一聲，你是男是女"
        message="卦上的財與官，男女取法不同 —— 財是男人的妻，官是女人的夫。不說清楚，老夫取錯了用神，這一卦就白搖了。"
        confirmTxt="去填寫"
        cancelTxt="稍後"
        onSuccess={() => {
          setNeedGender(false);
          navigate("/personal-editor");
        }}
        onClose={() => setNeedGender(false)}
      />
    </div>
  );
};

export default FuyaoziChat;
